import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError, inr, formatDate } from "@/lib/api";
import { PageHeader, StatusChip } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { toast, Toaster } from "sonner";
import { Plus, CheckCircle, XCircle, HandCoins, ArrowsClockwise } from "@phosphor-icons/react";

export default function EmployeeAdvances() {
    const { user } = useAuth();
    const [advances, setAdvances] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [ledgers, setLedgers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newOpen, setNewOpen] = useState(false);
    const [approveTarget, setApproveTarget] = useState(null);
    const [disburseTarget, setDisburseTarget] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [a, e, lg] = await Promise.all([
                api.get("/employee-advances"),
                api.get("/employees"),
                api.get("/ledgers"),
            ]);
            setAdvances(a.data);
            setEmployees(e.data);
            setLedgers(lg.data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { refresh(); }, [refresh]);

    const pending = advances.filter((x) => x.status === "pending");
    const approved = advances.filter((x) => x.status === "approved");
    const pendingBank = advances.filter((x) => x.status === "pending_bank_disbursement");
    const active = advances.filter((x) => x.status === "disbursed");
    const closed = advances.filter((x) => ["closed", "rejected", "cancelled"].includes(x.status));
    const bankCash = useMemo(() => ledgers.filter((l) => ["bank", "cash"].includes(l.party_type)), [ledgers]);
    const totalOutstanding = active.reduce((s, a) => s + (a.balance_outstanding || 0), 0);
    const isFounder = user?.role === "founder";

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Employee advances"
                sub="Personal money loans to employees. Approve, disburse, and let payroll auto-recover the EMI every month."
                right={
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={refresh} disabled={loading} data-testid="ea-refresh">
                            <ArrowsClockwise size={14} className="mr-2" /> Refresh
                        </Button>
                        <Button onClick={() => setNewOpen(true)} style={{ background: "#f59e0b", color: "#fff" }} data-testid="ea-new-btn">
                            <Plus size={14} className="mr-2" /> New advance request
                        </Button>
                    </div>
                }
            />

            <div className="p-8 space-y-6">
                <div className="grid grid-cols-4 gap-4">
                    <SummaryCard label="Pending approval" value={pending.length} isCount tone="draft" />
                    <SummaryCard label="Awaiting disbursement" value={approved.length} isCount tone="teal" />
                    <SummaryCard label="Active EMIs" value={active.length} isCount />
                    <SummaryCard label="Total outstanding" value={totalOutstanding} bold tone="amber" />
                </div>

                {isFounder && pending.length > 0 ? (
                    <Section title={`Pending approval — ${pending.length}`} accent="#f59e0b">
                        <AdvanceTable rows={pending} showActions="approve" onApprove={setApproveTarget}
                            onReject={async (a) => {
                                const reason = window.prompt("Reject reason?");
                                if (!reason) return;
                                try { await api.post(`/employee-advances/${a.id}/reject`, { reason }); toast.success("Rejected"); refresh(); }
                                catch (e) { toast.error(formatApiError(e)); }
                            }} />
                    </Section>
                ) : null}

                {approved.length > 0 ? (
                    <Section title={`Approved — waiting for disbursement · ${approved.length}`} accent="#0d6efd">
                        <AdvanceTable rows={approved} showActions="disburse" onDisburse={setDisburseTarget} />
                    </Section>
                ) : null}

                {pendingBank.length > 0 ? (
                    <Section title={`Queued for bank match · ${pendingBank.length}`} accent="#f59e0b">
                        <AdvanceTable rows={pendingBank} showActions="pending_bank"
                            onDiscard={async (a) => {
                                if (!window.confirm(`Discard the queued disbursement for ${a.employee_name}? This just cancels the intent — no journal was posted.`)) return;
                                try { await api.post(`/employee-advances/${a.id}/discard-intent`); toast.success("Discarded"); refresh(); }
                                catch (e) { toast.error(formatApiError(e)); }
                            }} />
                    </Section>
                ) : null}

                <Section title={`Active EMI schedules — ${active.length}`}>
                    <AdvanceTable rows={active} showActions="none" />
                </Section>

                <Section title={`Closed / Rejected / Cancelled — ${closed.length}`}>
                    <AdvanceTable rows={closed.slice(0, 20)} showActions="none" showRejection />
                </Section>
            </div>

            <NewAdvanceDialog open={newOpen} onOpenChange={setNewOpen} onSaved={refresh} employees={employees} />
            <ApproveDialog target={approveTarget} onClose={() => setApproveTarget(null)} onSaved={refresh} />
            <DisburseDialog target={disburseTarget} onClose={() => setDisburseTarget(null)}
                onSaved={refresh} bankCash={bankCash} />
        </>
    );
}

function AdvanceTable({ rows, showActions, onApprove, onReject, onDisburse, onDiscard, showRejection }) {
    if (rows.length === 0) return <div className="p-6 text-center text-stone-500 text-sm">No records.</div>;
    return (
        <table className="se-table">
            <thead>
                <tr>
                    <th>Employee</th><th className="text-right">Amount</th><th>Reason</th>
                    <th>EMI plan</th><th className="text-right">Outstanding</th><th>Status</th><th></th>
                </tr>
            </thead>
            <tbody>
                {rows.map((a) => (
                    <tr key={a.id} data-testid={`ea-row-${a.id}`}>
                        <td>{a.employee_name} <span className="text-[10px] text-stone-500">({a.employee_code})</span></td>
                        <td className="se-num text-right font-medium">₹ {inr(a.amount)}</td>
                        <td className="text-xs">{a.reason}</td>
                        <td className="text-xs">
                            {a.emi_amount ? `${a.emi_count} × ₹${inr(a.emi_amount)} from ${a.first_emi_month}` : "—"}
                        </td>
                        <td className="se-num text-right">
                            {a.status === "disbursed" ? `₹ ${inr(a.balance_outstanding || 0)}` : "—"}
                        </td>
                        <td>
                            <StatusChip status={a.status} />
                            {a.status === "pending_bank_disbursement" && a.intended_source_ledger_name
                                ? <div className="text-[10px] text-stone-500 mt-1">
                                    → {a.intended_source_ledger_name}{a.ref_no ? ` · ${a.ref_no}` : ""}
                                  </div> : null}
                            {showRejection && a.rejection_reason
                                ? <div className="text-[10px] text-stone-500 mt-1">{a.rejection_reason}</div> : null}
                        </td>
                        <td>
                            <div className="flex gap-2 justify-end">
                                {showActions === "approve" ? (
                                    <>
                                        <Button size="sm" variant="outline" onClick={() => onReject(a)} data-testid={`ea-reject-${a.id}`}>
                                            <XCircle size={14} className="mr-1" /> Reject
                                        </Button>
                                        <Button size="sm" onClick={() => onApprove(a)} style={{ background: "#16a34a", color: "#fff" }} data-testid={`ea-approve-${a.id}`}>
                                            <CheckCircle size={14} className="mr-1" /> Approve
                                        </Button>
                                    </>
                                ) : null}
                                {showActions === "disburse" ? (
                                    <Button size="sm" onClick={() => onDisburse(a)} style={{ background: "#0d6efd", color: "#fff" }} data-testid={`ea-disburse-${a.id}`}>
                                        <HandCoins size={14} className="mr-1" /> Disburse
                                    </Button>
                                ) : null}
                                {showActions === "pending_bank" ? (
                                    <Button size="sm" variant="outline" onClick={() => onDiscard(a)} data-testid={`ea-discard-${a.id}`}>
                                        <XCircle size={14} className="mr-1" /> Discard intent
                                    </Button>
                                ) : null}
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function NewAdvanceDialog({ open, onOpenChange, onSaved, employees }) {
    const [empId, setEmpId] = useState("");
    const [amount, setAmount] = useState("");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (open) { setEmpId(""); setAmount(""); setReason(""); } }, [open]);
    const submit = async () => {
        if (!empId) return toast.error("Pick employee");
        if (!Number(amount) || Number(amount) <= 0) return toast.error("Amount must be > 0");
        if (!reason.trim()) return toast.error("Reason required");
        setSaving(true);
        try {
            await api.post("/employee-advances", { employee_id: empId, amount: Number(amount), reason });
            toast.success("Request created — awaiting founder approval");
            onOpenChange(false);
            onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="ea-new-dialog">
                <DialogHeader><DialogTitle>New employee advance request</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div><Label>Employee *</Label>
                        <select className="w-full border rounded-sm px-3 py-2 text-sm" value={empId}
                                onChange={(e) => setEmpId(e.target.value)} data-testid="ea-emp">
                            <option value="">— Select —</option>
                            {employees.filter((e) => e.active).map((e) => (
                                <option key={e.id} value={e.id}>{e.name} ({e.employee_code}) · base ₹{inr(e.base_salary)}</option>
                            ))}
                        </select></div>
                    <div><Label>Amount (₹) *</Label>
                        <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="ea-amount" /></div>
                    <div><Label>Reason *</Label>
                        <Input value={reason} onChange={(e) => setReason(e.target.value)}
                               placeholder="e.g. Medical expense" data-testid="ea-reason" /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} style={{ background: "#f59e0b", color: "#fff" }} data-testid="ea-submit">
                        {saving ? "…" : "Send for approval"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ApproveDialog({ target, onClose, onSaved }) {
    const [emiCount, setEmiCount] = useState("");
    const [emiAmount, setEmiAmount] = useState("");
    const [firstMonth, setFirstMonth] = useState("");
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (target) {
            setEmiCount(""); setEmiAmount(""); setFirstMonth(defaultNextMonth());
        }
    }, [target]);

    // Two-way binding: whichever field the user edits drives the other. We
    // never derive via useEffect — that fought the user's backspace and made
    // the up/down stepper feel dead. Instead, both onChange handlers compute
    // the paired field directly. Blank input → blank paired field (so
    // backspace to empty simply clears both).
    const onCountChange = (v) => {
        setEmiCount(v);
        if (!target) return;
        if (v === "" || v === "-") { setEmiAmount(""); return; }
        const c = Number(v);
        if (c > 0) setEmiAmount(String(Math.round((target.amount / c) * 100) / 100));
    };
    const onAmountChange = (v) => {
        setEmiAmount(v);
        if (!target) return;
        if (v === "" || v === "-") { setEmiCount(""); return; }
        const a = Number(v);
        if (a > 0) setEmiCount(String(Math.ceil(target.amount / a)));
    };

    const submit = async () => {
        setSaving(true);
        try {
            await api.post(`/employee-advances/${target.id}/approve`, {
                emi_count: Number(emiCount) || null,
                emi_amount: Number(emiAmount) || null,
                first_emi_month: firstMonth || null,
            });
            toast.success("Approved — accountant can now disburse");
            onClose();
            onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };
    if (!target) return null;
    return (
        <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="ea-approve-dialog">
                <DialogHeader><DialogTitle>Approve advance — {target.employee_name}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div className="text-sm text-stone-600">
                        Amount: <b>₹ {inr(target.amount)}</b> · Reason: {target.reason}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div><Label>EMI count</Label>
                            <Input type="number" min="1" value={emiCount} onChange={(e) => onCountChange(e.target.value)}
                                   placeholder="e.g. 10" data-testid="ea-emi-count" /></div>
                        <div><Label>EMI amount (₹)</Label>
                            <Input type="number" min="1" value={emiAmount} onChange={(e) => onAmountChange(e.target.value)}
                                   placeholder="e.g. 5000" data-testid="ea-emi-amount" /></div>
                    </div>
                    <div><Label>First EMI month</Label>
                        <Input type="month" value={firstMonth} onChange={(e) => setFirstMonth(e.target.value)}
                               data-testid="ea-first-month" /></div>
                    <div className="text-[11px] text-stone-500">
                        Fill either count OR amount — the other is auto-computed. Recovery starts from the month above.
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} style={{ background: "#16a34a", color: "#fff" }} data-testid="ea-approve-submit">
                        {saving ? "…" : "Approve"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function DisburseDialog({ target, onClose, onSaved, bankCash }) {
    const [src, setSrc] = useState("");
    const [d, setD] = useState(new Date().toISOString().slice(0, 10));
    const [refNo, setRefNo] = useState("");
    const [saving, setSaving] = useState(false);

    // Group sources: system ledgers (Cash In Hand, Fiat Bank) settle immediately;
    // real banks (system=false) go through the bank-statement match flow.
    const { immediate, realBanks } = useMemo(() => {
        const imm = bankCash.filter((l) => l.system);
        const rb = bankCash.filter((l) => !l.system);
        return { immediate: imm, realBanks: rb };
    }, [bankCash]);

    useEffect(() => {
        if (target && bankCash.length && !src) {
            setSrc(immediate.find((l) => l.name === "FIAT BANK")?.id
                   || immediate[0]?.id
                   || bankCash[0].id);
        }
    }, [target, bankCash, src, immediate]);

    if (!target) return null;
    const chosen = bankCash.find((l) => l.id === src);
    const isRealBank = chosen && !chosen.system;

    const submit = async () => {
        setSaving(true);
        try {
            const { data } = await api.post(`/employee-advances/${target.id}/disburse`, {
                source_ledger_id: src, date: d,
                expected_amount: target.amount,
                ref_no: refNo || null,
            });
            if (data.mode === "pending_bank_match") {
                toast.success(
                    `Queued ₹${inr(target.amount)} against ${data.bank_ledger_name} — will post when the accountant matches this line during bank-statement upload.`
                );
            } else {
                toast.success(`Disbursed ₹${inr(target.amount)} to ${target.employee_name}`);
            }
            onClose(); onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="ea-disburse-dialog">
                <DialogHeader>
                    <DialogTitle>Disburse ₹{inr(target.amount)} to {target.employee_name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label>Paid from</Label>
                        <select className="w-full border rounded-sm px-3 py-2 text-sm" value={src}
                                onChange={(e) => setSrc(e.target.value)} data-testid="ea-src">
                            {immediate.length > 0 ? (
                                <optgroup label="Immediate settlement">
                                    {immediate.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                                </optgroup>
                            ) : null}
                            {realBanks.length > 0 ? (
                                <optgroup label="Bank (wait for statement)">
                                    {realBanks.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                                </optgroup>
                            ) : null}
                        </select>
                    </div>
                    <div><Label>{isRealBank ? "Expected transfer date" : "Date"}</Label>
                        <Input type="date" value={d} onChange={(e) => setD(e.target.value)} data-testid="ea-date" /></div>
                    {isRealBank ? (
                        <>
                            <div><Label>UTR / reference no <span className="text-stone-400 text-[11px]">(optional, helps accountant find the debit)</span></Label>
                                <Input value={refNo} onChange={(e) => setRefNo(e.target.value)}
                                       placeholder="e.g. NEFT UTR" data-testid="ea-ref-no" /></div>
                            <div className="border rounded-sm p-3 text-xs"
                                 style={{ borderColor: "#bae6fd", background: "#eff6ff", color: "#1e3a8a" }}
                                 data-testid="ea-real-bank-note">
                                <div className="font-medium mb-1">Real bank workflow</div>
                                <div>
                                    This will queue the advance against <b>{chosen?.name}</b>. Nothing hits the ledger yet.
                                    When the accountant uploads {chosen?.name}&apos;s statement, this payout will appear as a match candidate for the corresponding debit line — one click posts the journal and unlocks EMI recovery.
                                </div>
                            </div>
                        </>
                    ) : null}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} style={{ background: isRealBank ? "#f59e0b" : "#0d6efd", color: "#fff" }} data-testid="ea-disburse-submit">
                        {saving ? "…" : (isRealBank ? "Queue for bank match" : "Post disbursement")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Section({ title, children, accent }) {
    return (
        <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
            <div className="px-5 py-3 border-b" style={{ borderColor: "var(--se-border)" }}>
                <div className="font-serif text-lg" style={accent ? { color: accent } : undefined}>{title}</div>
            </div>
            {children}
        </div>
    );
}

function SummaryCard({ label, value, hint, tone, bold, isCount }) {
    const color = tone === "amber" ? "#b45309" : tone === "teal" ? "#0f766e" : tone === "draft" ? "#9a3412" : "#0f172a";
    return (
        <div className="border rounded-md px-5 py-4" style={{ borderColor: "var(--se-border)" }}>
            <div className="text-[11px] uppercase tracking-widest text-stone-500">{label}</div>
            <div className={`se-num text-2xl mt-1 ${bold ? "font-medium" : ""}`} style={{ color }}>
                {isCount ? value : `₹ ${inr(value || 0)}`}
            </div>
            {hint ? <div className="text-[11px] text-stone-500 mt-1">{hint}</div> : null}
        </div>
    );
}

function defaultNextMonth() {
    const d = new Date(); d.setMonth(d.getMonth() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
